import './index.scss';
import LogoColor from 'src/assets/svg/logo_color.svg?react';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, Button, Card, Checkbox, Col, Divider, Form, Input, Row, Spin } from 'antd';
import { LockOutlined, UserOutlined } from '@ant-design/icons';
import { jsonBase64Decode } from 'src/utils';
import { nameof } from 'src/shared/utils/objects';
import { IconChevronLeft } from '@tabler/icons-react';
import { useLocation, useNavigate } from 'react-router-dom';
import axios, { AxiosError } from 'axios';
import { Title, Text, Link } from 'src/shared/components/typography';
import { useBackHandler } from 'src/common/use-back-handler';
import { useMount } from 'ahooks';
// import { useClosestMedia } from 'src/shared/hooks/media-queries';

interface LoginFormValues {
  username: string;
  password: string;
  remember: boolean;
}

interface LoginViewModel {
  username: string;
  returnUrl: string;
  allowRememberLogin: boolean;
  enableLocalLogin: boolean;

  externalProviders: {
    displayName: string;
    authenticationScheme: string;
  }[];
}

const IndexView: React.FC<any> = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const handleBack = useBackHandler();
  const location = useLocation();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [errorMsg, setErrorMsg] = useState<string | undefined>();
  const [externalProviders, setExternalProviders] = useState<{ authenticationScheme: string; displayName: string }[]>([]);

  const loadExternalProviders = async () => {
    setIsLoading(true);
    try {
      const result = await axios.get<{ authenticationScheme: string; displayName: string }[]>('/externalProviders');
      if (result?.status === 200 && Array.isArray(result.data)) {
        setExternalProviders(result.data);
      }
    } catch (e) {
      console.error('External Providers', e);
    } finally {
      setIsLoading(false);
    }
  };

  useMount(() => {
    loadExternalProviders();
  });

  // const closestMedia = useClosestMedia();
  // const isSmallView = ['xs', 'sm'].includes(closestMedia as string);

  const dataQs = new URLSearchParams(location?.search ?? '').get('data') as string;
  const data = jsonBase64Decode<LoginViewModel>(dataQs, {
    enableLocalLogin: true,
    allowRememberLogin: true,
    externalProviders: externalProviders
  } as LoginViewModel);

  const visibleExternalProviders = data.externalProviders.filter((x) => (x.displayName || '').trim().length > 0);

  const onFinish = async (values: LoginFormValues) => {
    setErrorMsg(undefined);
    setIsLoading(true);

    try {
      const result = await axios.post<{ isNativeClient: boolean; returnUrl: string; message: string }>('/account/login', {
        username: values.username,
        password: values.password,
        rememberLogin: values.remember,
        returnUrl: data.returnUrl
      });
      if (result?.status === 200) {
        window.location.href = result.data.returnUrl ?? data.returnUrl;
      } else {
        const msg = result?.data?.message ?? (result as any as AxiosError<{ message: string }>)?.response?.data?.message ?? t('Error inesperado');
        setErrorMsg(msg);
        setIsLoading(false);
      }
    } catch (e) {
      console.error('Login', e);
      const axiosError = e as AxiosError<{ message: string }>;
      setErrorMsg(axiosError?.response?.data?.message?.toString() ?? (t('Error inesperado') as string));
      setIsLoading(false);
    }
  };

  return (
    <div id="login-container">
      <div id="logo-container">
        <span title="back-cta" role="img" id="back-cta" className={isLoading ? 'anticon disabled' : 'anticon'}>
          <IconChevronLeft onClick={isLoading ? undefined : handleBack} size={20} strokeWidth={3} color={isLoading ? 'lightgrey' : '#22817a'} />
        </span>
        <LogoColor />
      </div>
      <div id="form-container">
        <Spin spinning={isLoading} size={'large'}>
          <Card>
            <Title style={{ fontSize: 24 }} level={4}>
              {t('¡Bienvenido de nuevo!')}
            </Title>
            <Text style={{ fontSize: 16 }}>{t('Experimenta la libertad de elegir otra manera de trabajar')}</Text>
            {(errorMsg || '').trim().length > 0 && (
              <Row style={{ marginTop: 24 }}>
                <Col span={24}>
                  <Alert closable onClose={() => setErrorMsg(undefined)} description={errorMsg} type="error" />
                </Col>
              </Row>
            )}
            {data.enableLocalLogin && (
              <Form id="login" name="login" style={{ maxWidth: '100%', marginTop: 28 }} initialValues={{ remember: true }} onFinish={onFinish}>
                <Form.Item hidden initialValue={data.returnUrl} />
                <Row>
                  <Col span={24}>
                    <Form.Item
                      style={{ marginBottom: 32 }}
                      name={nameof<LoginFormValues>('username')}
                      rules={[{ required: true, message: t('¡Por favor ingrese su nombre de usuario!') as string }]}
                    >
                      <Input
                        className="j2l-form-big-input"
                        disabled={isLoading}
                        size="large"
                        placeholder={t('Correo electrónico o usuario/alias') as string}
                        prefix={<UserOutlined />}
                        type="email"
                        autoComplete="username"
                        name="username"
                        id="username"
                      />
                    </Form.Item>
                  </Col>
                </Row>
                <Row>
                  <Col span={24}>
                    <Form.Item
                      style={{ marginBottom: 26 }}
                      name={nameof<LoginFormValues>('password')}
                      rules={[{ required: true, message: t('¡Por favor ingrese su contraseña!') as string }]}
                    >
                      <Input.Password
                        className="j2l-form-big-input"
                        disabled={isLoading}
                        size="large"
                        placeholder={t('Contraseña') as string}
                        prefix={<LockOutlined />}
                        type="password"
                        id="password"
                        name="password"
                        autoComplete="current-password"
                      />
                    </Form.Item>
                  </Col>
                </Row>

                <Row>
                  {data.allowRememberLogin && (
                    <Col span={12}>
                      <Form.Item style={{ marginBottom: 34 }} name={nameof<LoginFormValues>('remember')} valuePropName="checked">
                        <Checkbox disabled={isLoading}>
                          <Text style={{ fontSize: 16 }} underline>
                            {t('Recuerda mis datos')}
                          </Text>
                        </Checkbox>
                      </Form.Item>
                    </Col>
                  )}
                  <Col span={12} style={data.allowRememberLogin ? { textAlign: 'right' } : undefined}>
                    <Form.Item style={{ marginBottom: 34 }}>
                      <Link onClick={() => navigate('/forgot-password')} color="#1A1C21" fontSize={16} underline disabled={isLoading}>
                        {t('Olvidé mi contraseña')}
                      </Link>
                    </Form.Item>
                  </Col>
                </Row>

                <Row>
                  <Col span={24}>
                    <Button
                      disabled={isLoading}
                      shape="round"
                      style={{ width: '100%', border: 'none' }}
                      size="large"
                      type="primary"
                      htmlType="submit"
                    >
                      {t('Inicia sesión')}
                    </Button>
                  </Col>
                </Row>
              </Form>
            )}
            {data.enableLocalLogin && visibleExternalProviders.length > 0 && (
              <Divider plain style={{ marginTop: 35, marginBottom: 12 }}>
                {t('o continúa con')}
              </Divider>
            )}

            {visibleExternalProviders.length > 0 && (
              <Row
                gutter={[16, 16]}
                justify="center"
                style={{ display: 'flex', rowGap: 16, columnGap: 16 /*, flexDirection: isSmallView ? 'column' : 'row'*/ }}
              >
                {visibleExternalProviders.map(({ displayName, authenticationScheme }) => {
                  const providerUrl = `${window.REACT_APP_API_BASE_URI}/external/ExternalLogin?scheme=${encodeURIComponent(
                    authenticationScheme
                  )}&returnUrl=${encodeURIComponent(data.returnUrl)}`;
                  return (
                    <div key={`${displayName}_${authenticationScheme}`} style={{ display: 'flex', textAlign: 'center' }}>
                      {/* La navegacion va en onClick y no se deja al href. antd 6 le quita el href al
                        ancla cuando el boton esta disabled (button.js: href: mergedDisabled ? undefined : ...),
                        y antd 5 no lo hacia. Como el propio onClick marca el estado que deshabilita el boton,
                        React re-renderiza antes de que el navegador evalue la navegacion por defecto, no
                        encuentra href y no navega: el spinner se queda para siempre. El href se conserva
                        para accesibilidad y para abrir en pestana nueva. */}
                      <Button
                        disabled={isLoading}
                        href={providerUrl}
                        ghost
                        type="default"
                        shape="round"
                        style={
                          !isLoading
                            ? { borderColor: '#D9D9D9', color: 'black', minWidth: 114 /*, width: isSmallView ? '100%' : undefined*/ }
                            : { borderColor: '#D9D9D9', color: '#00000040', minWidth: 114 /*, width: isSmallView ? '100%' : undefined*/ }
                        }
                        onClick={(e) => {
                          e.preventDefault();
                          setIsLoading(true);
                          window.location.assign(providerUrl);
                        }}
                      >
                        {displayName}
                      </Button>
                    </div>
                  );
                })}
              </Row>
            )}
            {data.enableLocalLogin || visibleExternalProviders.length > 0 ? (
              <>
                <Divider style={{ marginTop: 40, marginBottom: 39 }} />
                <Row>
                  <Col span={24}>
                    <Text fontSize={16}>{t('¿Aún no tienes cuenta en jobs2live?')}</Text>{' '}
                    <Link onClick={() => navigate('/registration')} disabled={isLoading} color="#1A1C21" fontSize={16} underline>
                      {t('Únete ahora')}
                    </Link>
                  </Col>
                </Row>
              </>
            ) : (
              <Row style={{ marginTop: 24 }}>
                <Col span={24}>
                  <Alert
                    description={t('No existe configuración adecuada para este pedido')}
                    message={t('Pedido de acceso inválido')}
                    type="warning"
                  />
                </Col>
              </Row>
            )}
          </Card>
        </Spin>
      </div>
    </div>
  );
};

export default IndexView;
